import { Component, type ErrorInfo, type ReactNode } from "react"
import * as Sentry from "@sentry/react"

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }
  /** Avoid double report: React StrictMode can invoke componentDidCatch twice in dev; Sentry may also get the same error from global handler. */
  private reportedError: Error | null = null

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo)
    if (this.reportedError !== error) {
      this.reportedError = error
      Sentry.captureException(error, { contexts: { react: errorInfo as any } })
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-stone-100 dark:bg-stone-900">
          <div className="max-w-lg w-full rounded-xl border border-red-200 dark:border-red-900/50 bg-white dark:bg-stone-800 p-6 shadow-lg">
            <h1 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">Something went wrong</h1>
            <p className="text-sm text-stone-600 dark:text-stone-400 font-mono break-all mb-4">
              {this.state.error.message}
            </p>
            <pre className="text-xs text-stone-500 dark:text-stone-500 overflow-auto max-h-48 p-3 rounded bg-stone-50 dark:bg-stone-900">
              {this.state.error.stack}
            </pre>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="mt-4 px-4 py-2 rounded-lg bg-stone-200 dark:bg-stone-700 text-stone-800 dark:text-stone-200 text-sm font-medium hover:bg-stone-300 dark:hover:bg-stone-600"
            >
              Try again
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
